import React, { useState } from 'react';

// ── GUIDE CONTENT BY ROLE ──

const APPRENTICE_GUIDE = {
  title: 'Apprentice Guide',
  intro: 'Welcome to SignedOff! This guide will help you understand how to log evidence, track your progress, and stay on top of your apprenticeship.',
  sections: [
    {
      heading: 'What is SignedOff?',
      content: `SignedOff is a web app that helps you collect and organise evidence for your apprenticeship qualification. Instead of keeping photos in your camera roll and scribbling notes in a logbook, everything goes into one place that your trainer and employer can see.`,
    },
    {
      heading: 'Submitting Evidence',
      steps: [
        { title: 'Tap "+ Add Evidence"', desc: 'You\'ll find this button on your Dashboard or Evidence Log page.' },
        { title: 'Fill in job details', desc: 'Enter the job number, vehicle/rego, and date of work. These help your trainer match evidence to real jobs.' },
        { title: 'Select units', desc: 'Choose which unit(s) this job covers. You\'ll see your progress for each unit — how many evidence entries are approved and how many more you need.' },
        { title: 'Describe the work', desc: 'Fill in the four guided fields: What was the fault/reason for the job? What did you do? What tools/equipment did you use? What was the outcome?' },
        { title: 'Add photos', desc: 'Take or upload up to 10 photos. Capture before, during, and after shots. Clear, well-lit photos make a big difference.' },
        { title: 'Submit', desc: 'Hit submit and your evidence goes to the review queue. Your trainer will approve or request changes.' },
      ],
    },
    {
      heading: 'What Makes Good Evidence?',
      tips: [
        'Take photos BEFORE you start — show the fault or starting condition.',
        'Take photos DURING — show yourself using the correct tools and techniques.',
        'Take photos AFTER — show the completed repair and final checks.',
        'Be specific in your descriptions. "Did a brake job" isn\'t enough. "Replaced front brake pads on a 2020 Volvo FH, measured disc thickness at 43.2mm (min 41mm), torqued caliper bolts to 120Nm" is excellent.',
        'Mention the tools you used and any specifications (torque values, measurements, fluid types).',
        'One good evidence entry with 5 clear photos beats three rushed ones with blurry shots.',
      ],
    },
    {
      heading: 'Knowledge Self-Assessment',
      content: `For each unit, there's a Knowledge pill you can toggle. This is your self-assessment — mark it when you feel confident you understand the theory behind the unit. Be honest with yourself — your trainer will assess this formally, but your self-assessment helps everyone see where you're at.`,
    },
    {
      heading: 'Tracking Your Progress',
      content: `Your Dashboard shows your overall progress. The Unit Progress page shows every unit in your training plan with three status pills: Knowledge (yours to toggle), Evidence (your trainer approves this), and Workplace (your supervisor confirms this). A unit is complete when all three are green. Check your progress regularly — don't wait for your trainer's visit to discover you're behind.`,
    },
    {
      heading: 'Trade School',
      content: `The Trade & School page shows your trade school attendance hours and any RTO workplace visits. Your employer or trainer logs these — you can view them to make sure everything's been recorded correctly.`,
    },
    {
      heading: 'Tips for Success',
      tips: [
        'Log evidence on the same day you do the work. Your memory is freshest and the truck is still in the bay for photos.',
        'Aim to submit at least one evidence entry per week. Consistency beats last-minute cramming.',
        'Read the evidence guide for each unit (it pops up when you select a unit). It tells you exactly what to photograph and write.',
        'If your evidence gets rejected, don\'t stress — read the trainer\'s feedback, fix it, and resubmit.',
        'Ask your supervisor to show you what they want to see before you start a job. That way you know what to capture.',
      ],
    },
  ],
};

const SUPERVISOR_GUIDE = {
  title: 'Supervisor Guide',
  intro: 'As a supervisor, you play a key role in confirming that apprentices are developing real workplace skills. This guide covers everything you need to know.',
  sections: [
    {
      heading: 'Your Role',
      content: `Supervisors are workshop-floor leaders who work directly with apprentices day-to-day. Your core responsibility is the Workplace sign-off — confirming that you've observed the apprentice demonstrating competency in a real workshop setting.`,
    },
    {
      heading: 'What You Can Do',
      permissions: [
        { action: 'View apprentice dashboard and progress', allowed: true },
        { action: 'View evidence log and submitted entries', allowed: true },
        { action: 'View unit progress for assigned apprentices', allowed: true },
        { action: 'Approve/toggle Workplace competency sign-off', allowed: true },
        { action: 'View review queue (pending evidence)', allowed: true },
        { action: 'View trade school and RTO visit records', allowed: true },
        { action: 'Generate and view progress reports', allowed: true },
        { action: 'Approve or reject evidence submissions', allowed: false },
        { action: 'Toggle Knowledge or Evidence sign-offs', allowed: false },
        { action: 'Upload or manage training plans', allowed: false },
        { action: 'Invite users or manage people', allowed: false },
      ],
    },
    {
      heading: 'Signing Off Workplace Competency',
      content: `For each unit of competency, three sign-offs are required: Knowledge (apprentice self-assesses), Evidence (trainer approves submitted evidence), and Workplace (you confirm real-world competency). Your Workplace sign-off is the third piece of the puzzle.`,
      steps: [
        { title: 'Go to Unit Progress', desc: 'Select the apprentice from the dropdown at the top of the page.' },
        { title: 'Find the unit', desc: 'Scroll to find the unit you want to sign off. Units are grouped by year level.' },
        { title: 'Click Workplace', desc: 'Each unit shows three pills: Knowledge, Evidence, and Workplace. Click the Workplace pill to toggle it green.' },
        { title: 'Done', desc: 'The sign-off saves immediately. Click again to undo if you made a mistake.' },
      ],
    },
    {
      heading: 'Understanding the Dashboard',
      content: `Your Dashboard shows a traffic-light overview of your assigned apprentices. Green means on track, amber means falling behind, and red means at risk. Use this proactively — a five-minute conversation when someone goes amber can prevent a six-month delay.`,
    },
    {
      heading: 'Tips for Supervisors',
      tips: [
        'Build sign-offs into your weekly rhythm. Five minutes at the end of each week keeps everything current.',
        'Only sign off when you\'ve genuinely observed the competency. This carries weight with the RTO.',
        'If an apprentice asks for a sign-off and you\'re not confident, tell them specifically what you need to see.',
        'Coordinate with the trainer — if evidence is approved but you haven\'t signed off Workplace, that\'s your prompt to observe.',
        'If you notice poor evidence quality, coach the apprentice on the spot. Better photos and descriptions help everyone.',
      ],
    },
  ],
};

const EMPLOYER_GUIDE = {
  title: 'Employer Guide',
  intro: 'As the employer, you have full visibility across all apprentices, supervisors, and trainers. This guide covers your dashboard, people management, and administrative tools.',
  sections: [
    {
      heading: 'Your Dashboard',
      content: `The Dashboard is your command centre. It shows every apprentice in your organisation with a traffic-light status (green/amber/red), unit progress bars, time used against contract, trade school attendance, evidence activity, and days remaining. If you have multiple sites, use the site filter dropdown to focus on one workshop at a time.`,
    },
    {
      heading: 'Managing People',
      content: `The Manage People page lets you invite new users, edit profiles, assign supervisors and trainers to apprentices, and manage your workshop sites.`,
      steps: [
        { title: 'Invite a user', desc: 'Click "+ Invite User", enter their name, email, and select their role (Apprentice, Supervisor, Trainer, or Employer). They\'ll receive a login link.' },
        { title: 'Assign supervisors', desc: 'In the Supervisors section, click "Assign" next to a supervisor. Tick the apprentices they should oversee. An apprentice can have multiple supervisors.' },
        { title: 'Assign trainers', desc: 'Same process in the Trainers section. Trainers only see apprentices assigned to them.' },
        { title: 'Edit profiles', desc: 'Click "Edit" on any user to update their name, student number, start date, or site assignment.' },
        { title: 'Manage sites', desc: 'Add workshop locations in the Sites section. Assign apprentices to sites when editing their profile.' },
      ],
    },
    {
      heading: 'Sites & Multi-Workshop',
      content: `If your business has multiple workshop locations, add each one as a Site in Manage People. Then assign apprentices to their site. The Dashboard site filter lets you view one workshop at a time, and each apprentice\'s site shows as a badge in the cohort table.`,
    },
    {
      heading: 'Evidence & Sign-offs',
      content: `As an employer, you can view all evidence, approve Workplace competency sign-offs (same as supervisors), and view the review queue. You can also toggle Evidence sign-offs. The three-way sign-off system ensures nothing falls through the cracks: Knowledge (apprentice), Evidence (trainer), and Workplace (employer or supervisor).`,
    },
    {
      heading: 'Training Plans & Bulk Upload',
      content: `The Training Plans page shows which units each apprentice is enrolled in. You can upload training plans from your RTO as PDFs or CSVs, and the system will extract unit data automatically. The Bulk Upload page handles CSV imports for trade school attendance and other data.`,
    },
    {
      heading: 'Reports',
      content: `Generate PDF progress reports for any apprentice with one click. Reports include unit tables with evidence counts, sign-off status, written assessment sections, and trade school data. These are ready to share with your RTO or use in progress meetings.`,
    },
    {
      heading: 'Tips for Employers',
      tips: [
        'Check the dashboard weekly. Catching an apprentice going amber early saves months of catch-up.',
        'Assign supervisors to apprentices so the people working alongside them daily can confirm competency.',
        'Use the site filter if you run multiple workshops — it keeps the dashboard manageable.',
        'Generate progress reports before RTO workplace visits so you\'re prepared.',
        'Encourage apprentices to log evidence on the day they do the work. Set the expectation from day one.',
        'Review pending evidence regularly so apprentices aren\'t waiting weeks for feedback.',
      ],
    },
  ],
};

const TRAINER_GUIDE = {
  title: 'Trainer Guide',
  intro: 'As a trainer, you review apprentice evidence, manage training plans, and approve Evidence sign-offs. This guide covers your key workflows.',
  sections: [
    {
      heading: 'Your Dashboard',
      content: `The Dashboard shows a cohort overview of your assigned apprentices with traffic-light status indicators. You can see unit progress, time used, trade school attendance, evidence activity, and days remaining at a glance. Click any apprentice to drill into their detail.`,
    },
    {
      heading: 'Reviewing Evidence',
      content: `The Review Queue is your primary workflow. It shows all pending evidence submissions from your assigned apprentices.`,
      steps: [
        { title: 'Open Review Queue', desc: 'Navigate to the Review Queue tab. You\'ll see a count of pending submissions in the nav badge.' },
        { title: 'Review each entry', desc: 'Each submission shows the job details, structured description (fault/reason, work performed, tools, outcome), and photos. Review these against the unit requirements.' },
        { title: 'Approve or reject', desc: 'If the evidence meets the standard, approve it. If it needs improvement, reject it with a note explaining what\'s missing. The apprentice will see your feedback.' },
        { title: 'Evidence sign-off', desc: 'Once enough evidence is approved for a unit (the threshold varies per unit), you can toggle the Evidence sign-off on the Unit Progress page.' },
      ],
    },
    {
      heading: 'Three-Way Sign-Off',
      content: `Each unit requires three independent sign-offs: Knowledge (apprentice self-assessment), Evidence (you approve sufficient evidence has been submitted), and Workplace (supervisor/employer confirms real-world competency). A unit is complete when all three are green. You control the Evidence sign-off.`,
    },
    {
      heading: 'Training Plans',
      content: `The Training Plans page shows which units each apprentice is enrolled in by year level. You can upload training plans from your RTO system — SignedOff accepts PDF and CSV formats and extracts unit data automatically.`,
    },
    {
      heading: 'Trade School & RTO Visits',
      content: `The Trade & School page tracks trade school hours against targets and logs RTO workplace visits. You can add entries manually or bulk-import attendance from CSV. This data flows into progress reports.`,
    },
    {
      heading: 'Bulk Upload',
      content: `The Bulk Upload page lets you import training plans, knowledge assessment results, trade school logs, and unit sign-offs from CSV files. The system detects column mappings automatically and shows a preview before importing.`,
    },
    {
      heading: 'Reports',
      content: `Generate comprehensive PDF progress reports for any apprentice. Reports include contract dates, unit tables by year with evidence counts and sign-off status, trade school hours, written assessment narrative, and sign-off blocks. These are designed to be shared with employers and used in formal progress reviews.`,
    },
    {
      heading: 'Tips for Trainers',
      tips: [
        'Process the review queue regularly — weekly at minimum. Apprentices lose motivation when evidence sits pending for weeks.',
        'When rejecting evidence, be specific about what\'s missing. "Need more detail" isn\'t helpful. "Please add the torque specification and a photo of the torque wrench on the bolt" is.',
        'Use the Evidence sign-off as a milestone conversation. When you sign off Evidence for a unit, it\'s a chance to discuss what the apprentice has learned.',
        'Check the dashboard before workplace visits so you know which apprentices need attention.',
        'Upload training plans and progress data early. The more data in the system, the more useful the dashboard and reports become.',
        'Coordinate with supervisors — if Workplace is signed off but Evidence isn\'t, prompt the apprentice to submit their evidence.',
      ],
    },
  ],
};

// ── GUIDE MAP BY ROLE ──
const GUIDE_MAP = {
  apprentice: APPRENTICE_GUIDE,
  supervisor: SUPERVISOR_GUIDE,
  employer: EMPLOYER_GUIDE,
  trainer: TRAINER_GUIDE,
};

const ALL_GUIDES = [
  { key: 'apprentice', label: 'Apprentice Guide' },
  { key: 'supervisor', label: 'Supervisor Guide' },
  { key: 'employer', label: 'Employer Guide' },
  { key: 'trainer', label: 'Trainer Guide' },
];

// ── RENDER ──

function GuideSection({ section }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>
        {section.heading}
      </h3>

      {section.content && (
        <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7, marginBottom: 12 }}>
          {section.content}
        </p>
      )}

      {section.steps && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {section.steps.map((step, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, alignItems: 'flex-start',
              background: 'var(--panel)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '10px 14px',
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                background: 'var(--accent)', color: '#fff', fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12,
              }}>{i + 1}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{step.title}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {section.tips && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {section.tips.map((tip, i) => (
            <div key={i} style={{
              display: 'flex', gap: 8, alignItems: 'flex-start',
              fontSize: 12.5, color: 'var(--text)', lineHeight: 1.6,
            }}>
              <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
              {tip}
            </div>
          ))}
        </div>
      )}

      {section.permissions && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th style={{ width: 80, textAlign: 'center' }}>Access</th>
              </tr>
            </thead>
            <tbody>
              {section.permissions.map((p, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 12 }}>{p.action}</td>
                  <td style={{
                    textAlign: 'center', fontWeight: 700, fontSize: 12,
                    color: p.allowed ? 'var(--green)' : 'var(--red)',
                    background: p.allowed ? 'rgba(39,174,96,.06)' : 'rgba(231,76,60,.06)',
                  }}>
                    {p.allowed ? 'Yes' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function HelpPage({ profile }) {
  const isAdmin = profile.role === 'employer' || profile.role === 'trainer';
  const defaultGuide = profile.role;
  const [activeGuide, setActiveGuide] = useState(defaultGuide);
  const guide = GUIDE_MAP[activeGuide];

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Help & Guides</div>
          <div className="page-sub">User guides for SignedOff</div>
        </div>
      </div>

      {/* Guide selector — employers and trainers can view all guides */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {ALL_GUIDES.map(g => (
            <button
              key={g.key}
              className={`btn xs ${activeGuide === g.key ? '' : 'outline'}`}
              onClick={() => setActiveGuide(g.key)}
              style={activeGuide === g.key ? { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' } : {}}
            >
              {g.label}
            </button>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-hdr">
          <span className="card-title">{guide.title}</span>
        </div>
        <div style={{ padding: '20px 20px 8px' }}>
          <p style={{
            fontSize: 13, color: 'var(--muted)', lineHeight: 1.7,
            marginBottom: 24, paddingBottom: 16,
            borderBottom: '1px solid var(--border)',
          }}>
            {guide.intro}
          </p>

          {guide.sections.map((section, i) => (
            <GuideSection key={i} section={section} />
          ))}
        </div>
      </div>
    </div>
  );
}
